import React, { useState, useContext, useEffect } from "react";
import {
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import api from "../api";

const RabbitLogin = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log("RabbitLogin component mounted with path:", location.pathname);
    // Clear any previous tokens on login page load
    if (location.pathname === "/rabbit") {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenType");

      // Clear authorization header
      if (api.defaults.headers.common["Authorization"]) {
        delete api.defaults.headers.common["Authorization"];
      }

      // Show error if redirected due to token expiration
      if (location.search.includes("expired=true")) {
        setError("Your session has expired. Please log in again.");
      }
    }
  }, [location.pathname, location.search]);

  // This prevents any navigation guards or redirects from affecting this route
  useEffect(() => {
    // Ensure we stay on the rabbit route if that's where we are
    if (location.pathname !== "/rabbit") {
      navigate("/rabbit", { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Try using the token endpoint first (JWT format)
      const response = await api.post("/api/auth/login/", formData, {
        withCredentials: true, // Ensure cookies are sent/received
      });

      // Handle both JWT format (access/refresh) and dj-rest-auth format (key)
      let accessToken, refreshToken;

      if (response.data.access) {
        // JWT format in response body
        accessToken = response.data.access;
        refreshToken = response.data.refresh || "";
      } else if (response.data.key) {
        // dj-rest-auth format - use the key as both access and refresh token
        accessToken = response.data.key;
        refreshToken = response.data.key;
      } else if (response.status === 200) {
        // Cookie-based auth - the token is in HTTP-only cookies
        // Use placeholder values for the auth context
        accessToken = "cookie-auth";
        refreshToken = "cookie-auth";
      } else {
        throw new Error("Invalid response format from server");
      }

      const loginSuccess = await login(accessToken, refreshToken);

      if (loginSuccess) {
        const redirectUrl = localStorage.getItem("redirectAfterLogin");
        localStorage.removeItem("redirectAfterLogin");
        const targetPath = redirectUrl || location.state?.from?.pathname || "/";
        navigate(targetPath, { replace: true });
      } else {
        throw new Error("Failed to authenticate with the server");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.non_field_errors?.[0] ||
          err.message ||
          "Failed to log in. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={6}>
          <div className="mt-5">
            <h2 className="text-center mb-4">Login</h2>

            {error && (
              <Alert variant="danger" onClose={() => setError("")} dismissible>
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  autoComplete="username"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  autoComplete="current-password"
                />
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                className="w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Logging in...
                  </>
                ) : (
                  "Login"
                )}
              </Button>

              <div className="text-center mt-3">
                <Link to="/forgot-password">Forgot Password?</Link>
              </div>
              <div className="text-center mt-2">
                <Link to="/register">Don't have an account? Register</Link>
              </div>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default RabbitLogin;
